import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import ReactDOM from 'react-dom';
import Button from '../Button';
import CloseIconSVG from '../Icons/CloseIcon';
import { Paragraph } from '../Typography';
import Breakpoints from '../../design-utils/breakpoints';
import { useTranslation } from 'react-i18next';

interface IModalProps {
  open: boolean;
  isCompleteScreen?: boolean;
  title?: string;
  onClose?: () => void;
  onAfterClose?: () => void;
  portalDiv?: string;
  children: React.ReactElement;
}

export default ({
  open,
  isCompleteScreen = false,
  children,
  title,
  portalDiv = 'bundleapp-subscriptions-portal',
  onClose,
  onAfterClose,
}: IModalProps): React.ReactElement | null => {
  useEffect(() => {
    if (modalRef && modalRef.current) {
      modalRef.current.focus();
    }
  }, []);
  const modalRef = useRef<any>(null);

  useEffect(() => {
    if (open) {
      document.body.classList.add(
        'bundleapp-freezeBody',
        'subscription-overlay-open',
      );
    }
    return () =>
      document.body.classList.remove(
        'bundleapp-freezeBody',
        'subscription-overlay-open',
      );
  }, [open]);

  const containerClass = `bundleapp-modal__container${
    isCompleteScreen ? '--complete-screen' : ''
  }`;

  if (!open) {
    return null;
  }

  const handleCloseClick = () => {
    if (onClose) onClose();
    if (onAfterClose) onAfterClose();
  };

  const portalEl = document.getElementById(portalDiv);

  const modalContent = (
    <div
      className="bundleapp-modal"
      role={isCompleteScreen ? 'main' : 'dialog'}
      aria-modal={isCompleteScreen ? 'false' : 'true'}
      aria-labelledby={isCompleteScreen ? '' : 'modal-title'}
      tabIndex={-1}
    >
      <div
        className="bundleapp-modal__backdrop"
        onClick={handleCloseClick}
      ></div>
      <div className={containerClass} ref={modalRef} tabIndex={0}>
        <header className="bundleapp-modal__container--complete-screen--header-container">
          <LogoContainer className="bundleapp-modal__logo" />
          <HideSm className="bundle-hide-sm">
            <Paragraph id="modal-title" modifiers={['body']}>
              {title}
            </Paragraph>
          </HideSm>
          {isCompleteScreen ? <Close onClick={handleCloseClick} /> : null}
        </header>
        {children}
      </div>
    </div>
  );
  if (portalEl) {
    return ReactDOM.createPortal(modalContent, portalEl);
  } else {
    const modalDiv = document.createElement('div');
    modalDiv.id = portalDiv;
    document.body.appendChild(modalDiv);
    return ReactDOM.createPortal(modalContent, modalDiv);
  }
};

export const Close: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { t } = useTranslation();

  return (
    <CloseButton
      onClick={onClick}
      aria-label={t('aria_labels.close_build_a_box')}
    >
      <CloseIcon className="bundle-close-icon--modal-close" />
    </CloseButton>
  );
};

const CloseButton = styled(Button)`
  position: absolute;

  cursor: pointer;
  width: 32px;
  min-width: fit-content;
  height: 32px;
  top: 40px;
  right: 40px;
  background-color: #f8f8f8;
  color: #000;
  border-radius: 50%;
  padding: 5px;
  font-size: 22px;
  font-weight: 300;
  transition: background-color 0.15s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;

  &:hover {
    background-color: #ebebeb;
  }
`;

const CloseIcon = styled(CloseIconSVG)`
  width: 24px;
  height: 24px;
`;

const LogoContainer = styled.div`
  position: absolute;
  width: 200px;
  height: 50px;
  background-size: contain;
  background-repeat: no-repeat;
  top: 20px;
  left: 40px;
`;

const HideSm = styled.div`
  @media screen and (max-width: ${Breakpoints.md}) {
    display: none;
  }
`;
